import BaseUIHandler from '../BaseUIHandler';
import BaseUISystem from '../BaseUISystem';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import Element from '../../../Prototype/UI/Element';
import ElementComponent from '../../../Component/UI/Element';
import Point from '../../../Utility/Point';
import Game from '../../../Game';
import Overlay from '../../../Prototype/UI/Overlay';
import PIXIRenderer from '../../Render/PIXIRenderer';
import RenderSystem, { RenderSystemEvent } from '../../Render/RenderSystem';
import { ResourceDriver } from '../../../Utility/Resource/ResourceManager';
import Scene from '../../../Scene/Scene';
import CrossPromoMenuButton from '../../../Prefabs/UI/HUD/CrossPromoMenuButton';
import CrossPromoDriver, { CrossPromoPlacementToAssetType } from '../../../Utility/CrossPromo/CrossPromoDriver';
import CrossPromoCampaign from '../../../Utility/CrossPromo/CrossPromoCampaign';
import { report } from '../../../report';
import { PopupID } from '../../../State/UI/PopupID';
import WorldSystem from '../../World/WorldSystem';

export default class CrossPromoHUDHandler extends BaseUIHandler {
    protected openMenuButton: CrossPromoMenuButton;
    protected closeBtn: Element;
    protected overlay: Overlay;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.CROSS_PROMO, uiSystem, false);
        this.alwaysVisible = true;
        this.system.scene.once('CrossPromoLoaded', () => {
            setTimeout(() => {
                this.onLoaded();
            }, 200);
        });
    }

    private handleXPromoOverlap(): void {
        const worldSystem: WorldSystem = Game.scene.getActiveScene().getSystem(WorldSystem);
        if(this.openMenuButton && worldSystem?.isXPromoIconOverlappingLevelNode(this.openMenuButton)) {
            this.openMenuButton.container.y = (720 - this.openMenuButton.container.y) * 1.5;
        }
    }

    public async preload(): Promise<void> {
        // Queue assets needed for each campaign
        const campaigns: CrossPromoCampaign[] = await CrossPromoDriver.getCampaigns();
        for (let i = 0; i < campaigns.length; i++) {
            const campaign: CrossPromoCampaign = campaigns[i];
            if (CrossPromoPlacementToAssetType.has(campaign.placement)) {
                campaign.queueAsset(
                    CrossPromoPlacementToAssetType.get(campaign.placement),
                    Game.sdk.getLanguage()
                );
            }
        }
        await super.preload();
    }
    protected onCreate() {
        super.onCreate();
        this.element.getComponent(ElementComponent).pivot.copy(Point.Zero);
        RenderSystem.on(RenderSystemEvent.RESIZE, this.onResize.bind(this));
    }
    private readonly onLoaded = (): void => {
        try {
            // Create overlay
            this.overlay = GameObject.Instantiate(Overlay, {
                name: 'cross_promo_overlay',
                preventInteraction: true,
                anchors: [0, 0, 0, 0],
                pivot: [0, 0],
                visible: false,
            });

            this.openMenuButton = GameObject.Instantiate(CrossPromoMenuButton, {
                name: 'open_cross_promo_button',
                layout: {
                    anchors: [0.938, 0.72, 0.938, 0.72],
                },
            });
            // this.openMenuButton.on(GameObjectEvent.SELECTED, this.onShow, this);

            this.closeBtn = GameObject.Instantiate(Element, {
                name: 'close_button',
                layout: {
                    anchors: [0.95, 0.1, 0.95, 0.1],
                },
                components: [
                    {
                        type: 'image',
                        data: {
                            cacheKey: 'button_close',
                        },
                    },
                    {
                        type: 'input',
                        data: {
                            enabled: true,
                            buttonMode: true,
                        },
                    },
                ],
                visible: false,
            });
            this.closeBtn.on(GameObjectEvent.SELECTED, this.onHide, this);
            this.element.addChild(this.overlay);
            this.element.addChild(this.openMenuButton);
            this.element.addChild(this.closeBtn);
            this.openMenuButton.idle();

            this.onResize();
        } catch (error) {
            report('Cross Promo HUD failed to load', {
                error,
                once: true,
                sampleRate: 0.1,
            });
        }
    };

    public onResize(): void {
        // Resize container and children
        if (this.element.destroyed) return;

        this.element.container.resize(
            PIXIRenderer.renderer.width,
            PIXIRenderer.renderer.height - PIXIRenderer.borderHeight * 2
        );
        this.element.children.forEach((child: Element) =>
            child.container.resize(
                PIXIRenderer.renderer.width,
                PIXIRenderer.renderer.height - PIXIRenderer.borderHeight * 2
            )
        );
        // Reposition container
        this.element.getComponent(ElementComponent).offsets = [
            0,
            -PIXIRenderer.borderHeight,
            0,
            -PIXIRenderer.borderHeight,
        ];

        this.handleXPromoOverlap();
    }

    public static async startLoading(scene: Scene): Promise<void> {
        await Game.resource.load(ResourceDriver.SECONDARY);
        scene.emit('CrossPromoLoaded');
    }
}
